import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import DashboardLayout from '../../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Table } from '../../../../components/ui/Table';
import { Modal } from '../../../../components/ui/Modal';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiPackage, FiAlertCircle, FiLink, FiX, FiCheckCircle } from 'react-icons/fi';
import {
  fetchWeapons,
  getWeaponCount,
  deleteWeapon,
  deployWeaponToClient,
  returnWeaponFromClient,
  WeaponData,
  WeaponDeploymentData,
} from '../service/weaponService';
import WeaponModal from './WeaponModal';
import WeaponDeploymentModal from './WeaponDeploymentModal';
import WeaponDocumentsModal from './WeaponDocumentsModal';
import { getWeaponDocuments } from '../service/weaponDocumentService';
import { fetchIndividualClients } from '../../../manage_clients/individual_clients/service/individualClientService';
import { fetchFactoryClients } from '../../../manage_clients/factory_clients/service/factoryClientService';
import { fetchBankClients } from '../../../manage_clients/bank_clients/service/bankClientService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  align-items: center;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'available':
        return theme.colors.accent.success + '20';
      case 'deployed':
        return theme.colors.primary[500] + '20';
      case 'maintenance':
        return theme.colors.accent.warning + '20';
      case 'retired':
        return theme.colors.light.background.tertiary;
      default:
        return theme.colors.light.background.tertiary;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'available':
        return theme.colors.accent.success;
      case 'deployed':
        return theme.colors.primary[600];
      case 'maintenance':
        return theme.colors.accent.warning;
      case 'retired':
        return theme.colors.light.text.secondary;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const ConditionBadge = styled.span<{ condition: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ condition }) => {
    switch (condition) {
      case 'excellent':
        return theme.colors.accent.success + '20';
      case 'good':
        return theme.colors.accent.info + '20';
      case 'fair':
        return theme.colors.accent.warning + '20';
      case 'poor':
        return theme.colors.accent.danger + '20';
      default:
        return theme.colors.light.background.tertiary;
    }
  }};
  color: ${({ condition }) => {
    switch (condition) {
      case 'excellent':
        return theme.colors.accent.success;
      case 'good':
        return theme.colors.accent.info;
      case 'fair':
        return theme.colors.accent.warning;
      case 'poor':
        return theme.colors.accent.danger;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.delete:hover {
    color: ${theme.colors.accent.danger};
    background-color: ${theme.colors.accent.danger}20;
  }

  &.deploy:hover {
    color: ${theme.colors.primary[600]};
    background-color: ${theme.colors.primary[50]};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const WeaponInventoryPage: React.FC = () => {
  const { user, companyData } = useAuth();
  const [weapons, setWeapons] = useState<WeaponData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWeapon, setSelectedWeapon] = useState<WeaponData | null>(null);
  const [showWeaponModal, setShowWeaponModal] = useState(false);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [weaponToDelete, setWeaponToDelete] = useState<WeaponData | null>(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedWeaponForDocuments, setSelectedWeaponForDocuments] = useState<WeaponData | null>(null);
  const [weaponDocumentStatus, setWeaponDocumentStatus] = useState<{ [weaponId: string]: boolean }>({});
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    deployed: 0,
    maintenance: 0,
    retired: 0,
  });

  const expectedCompanyId = '';
  const companyId = companyData?.id || expectedCompanyId;

  useEffect(() => {
    if (user) {
      loadWeapons();
      loadStats();
    }
  }, [user, companyData]);

  const loadWeapons = async () => {
    setLoading(true);
    try {
      const weaponsData = await fetchWeapons(companyId);
      setWeapons(weaponsData);
      
      // Check document status for each weapon
      const documentStatus: { [weaponId: string]: boolean } = {};
      for (const weapon of weaponsData) {
        try {
          const documents = await getWeaponDocuments(weapon.id, companyId);
          documentStatus[weapon.id] = documents && documents.length > 0;
        } catch (error) {
          console.error(`Error checking documents for weapon ${weapon.id}:`, error);
          documentStatus[weapon.id] = false;
        }
      }
      setWeaponDocumentStatus(documentStatus);
    } catch (error: any) {
      console.error('Error loading weapons:', error);
      toast.error(error.message || 'Failed to load weapons');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const weaponStats = await getWeaponCount(companyId);
      setStats(weaponStats);
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  };

  const filteredWeapons = useMemo(() => {
    if (!searchTerm.trim()) return weapons;
    const term = searchTerm.toLowerCase();
    return weapons.filter((w) => {
      return (
        w.weaponId?.toLowerCase().includes(term) ||
        w.weaponType?.toLowerCase().includes(term) ||
        w.serialNumber?.toLowerCase().includes(term) ||
        w.manufacturer?.toLowerCase().includes(term) ||
        w.model?.toLowerCase().includes(term)
      );
    });
  }, [weapons, searchTerm]);

  const handleDelete = async () => {
    if (!weaponToDelete) return;

    try {
      await deleteWeapon(weaponToDelete.id, companyId);
      toast.success('Weapon deleted successfully');
      setShowDeleteModal(false);
      setWeaponToDelete(null);
      await loadWeapons();
      await loadStats();
    } catch (error: any) {
      console.error('Error deleting weapon:', error);
      toast.error(error.message || 'Failed to delete weapon');
    }
  };

  const handleReturnWeapon = async (weaponId: string) => {
    try {
      await returnWeaponFromClient(weaponId, companyId);
      toast.success('Weapon returned successfully');
      await loadWeapons();
      await loadStats();
    } catch (error: any) {
      console.error('Error returning weapon:', error);
      toast.error(error.message || 'Failed to return weapon');
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Weapon Inventory</PageTitle>
        <Button onClick={() => {
          setSelectedWeapon(null);
          setShowWeaponModal(true);
        }}>
          <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
          Add Weapon
        </Button>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Weapons</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.success}>{stats.available}</StatValue>
            <StatLabel>Available</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.primary[600]}>{stats.deployed}</StatValue>
            <StatLabel>Deployed</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.warning}>{stats.maintenance}</StatValue>
            <StatLabel>Maintenance</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.light.text.secondary}>{stats.retired}</StatValue>
            <StatLabel>Retired</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          <SearchContainer>
            <SearchInputContainer>
              <Input
                placeholder="Search by weapon ID, type, serial number, manufacturer, or model..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                dark
                fullWidth
              />
            </SearchInputContainer>
          </SearchContainer>

          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading weapons...
            </div>
          ) : filteredWeapons.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Weapon ID</Table.HeaderCell>
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Serial Number</Table.HeaderCell>
                  <Table.HeaderCell>Manufacturer</Table.HeaderCell>
                  <Table.HeaderCell>Model</Table.HeaderCell>
                  <Table.HeaderCell>Condition</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Current Deployment</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredWeapons.map((weapon) => (
                  <Table.Row key={weapon.id}>
                    <Table.Cell>{weapon.weaponId || '—'}</Table.Cell>
                    <Table.Cell>{weapon.weaponType || '—'}</Table.Cell>
                    <Table.Cell>{weapon.serialNumber || '—'}</Table.Cell>
                    <Table.Cell>{weapon.manufacturer || '—'}</Table.Cell>
                    <Table.Cell>{weapon.model || '—'}</Table.Cell>
                    <Table.Cell>
                      <ConditionBadge condition={weapon.condition || 'good'}>
                        {weapon.condition || 'Good'}
                      </ConditionBadge>
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={weapon.status || 'available'}>
                        {weapon.status || 'Available'}
                      </StatusBadge>
                    </Table.Cell>
                    <Table.Cell>
                      {weapon.currentDeployment ? (
                        <div>
                          <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
                            {weapon.currentDeployment.clientName}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                            {weapon.currentDeployment.clientType}
                          </div>
                          <div style={{ marginTop: theme.spacing[1], display: 'flex', alignItems: 'center', gap: theme.spacing[1] }}>
                            {weaponDocumentStatus[weapon.id] ? (
                              <>
                                <FiCheckCircle size={14} style={{ color: theme.colors.accent.success }} />
                                <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.accent.success }}>
                                  Documents
                                </span>
                              </>
                            ) : (
                              <>
                                <FiX size={14} style={{ color: theme.colors.accent.warning }} />
                                <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.accent.warning }}>
                                  Pending Documents
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', gap: theme.spacing[1] }}>
                        {weapon.currentDeployment && (
                          <ActionButton
                            className={!weaponDocumentStatus[weapon.id] ? 'deploy' : ''}
                            onClick={() => {
                              setSelectedWeaponForDocuments(weapon);
                              setShowDocumentsModal(true);
                            }}
                            title="Attach Documents"
                            style={{
                              color: !weaponDocumentStatus[weapon.id] ? theme.colors.accent.warning : theme.colors.light.text.secondary,
                            }}
                          >
                            <FiLink size={18} />
                          </ActionButton>
                        )}
                        <ActionButton
                          onClick={() => {
                            setSelectedWeapon(weapon);
                            setShowHistoryModal(true);
                          }}
                          title="View History"
                        >
                          <FiEye size={18} />
                        </ActionButton>
                        <ActionButton
                          onClick={() => {
                            setSelectedWeapon(weapon);
                            setShowWeaponModal(true);
                          }}
                          title="Edit"
                        >
                          <FiEdit size={18} />
                        </ActionButton>
                        {weapon.status === 'available' && (
                          <ActionButton
                            className="deploy"
                            onClick={() => {
                              setSelectedWeapon(weapon);
                              setShowDeploymentModal(true);
                            }}
                            title="Deploy"
                          >
                            <FiPackage size={18} />
                          </ActionButton>
                        )}
                        {weapon.status === 'deployed' && (
                          <ActionButton
                            className="deploy"
                            onClick={() => handleReturnWeapon(weapon.id)}
                            title="Return"
                          >
                            <FiPackage size={18} />
                          </ActionButton>
                        )}
                        <ActionButton
                          className="delete"
                          onClick={() => {
                            setWeaponToDelete(weapon);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                        >
                          <FiTrash2 size={18} />
                        </ActionButton>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <FiPackage size={48} style={{ marginBottom: theme.spacing[2], opacity: 0.5 }} />
              <p>No weapons found. Add your first weapon to get started.</p>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <WeaponModal
        isOpen={showWeaponModal}
        onClose={() => {
          setShowWeaponModal(false);
          setSelectedWeapon(null);
        }}
        weapon={selectedWeapon}
        companyId={companyId}
        onSave={() => {
          loadWeapons();
          loadStats();
        }}
      />

      <WeaponDeploymentModal
        isOpen={showDeploymentModal}
        onClose={() => {
          setShowDeploymentModal(false);
          setSelectedWeapon(null);
        }}
        weapon={selectedWeapon}
        companyId={companyId}
        onDeploy={() => {
          loadWeapons();
          loadStats();
        }}
      />

      {selectedWeaponForDocuments && (
        <WeaponDocumentsModal
          isOpen={showDocumentsModal}
          onClose={() => {
            setShowDocumentsModal(false);
            setSelectedWeaponForDocuments(null);
          }}
          weaponId={selectedWeaponForDocuments.id}
          weaponName={selectedWeaponForDocuments.weaponId || 'Weapon'}
          companyId={companyId}
          onDocumentsUpdated={() => {
            loadWeapons();
          }}
        />
      )}

      {selectedWeapon && (
        <Modal
          isOpen={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedWeapon(null);
          }}
          title={`Deployment History - ${selectedWeapon.weaponId}`}
          size="lg"
          footer={
            <Button variant="secondary" onClick={() => {
              setShowHistoryModal(false);
              setSelectedWeapon(null);
            }}>
              Close
            </Button>
          }
        >
          {selectedWeapon.currentDeployment && (
            <div style={{ marginBottom: theme.spacing[4] }}>
              <h3 style={{ 
                fontSize: theme.typography.fontSize.lg, 
                marginBottom: theme.spacing[2],
                color: theme.colors.light.text.primary,
                fontWeight: theme.typography.fontWeight.bold,
              }}>
                Current Deployment
              </h3>
              <div style={{
                padding: theme.spacing[3],
                background: theme.colors.light.background.secondary,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.light.border.light}`,
              }}>
                <p style={{ color: theme.colors.light.text.primary, marginBottom: theme.spacing[2] }}>
                  <strong style={{ color: theme.colors.light.text.primary }}>Client:</strong> {selectedWeapon.currentDeployment.clientName}
                </p>
                <p style={{ color: theme.colors.light.text.primary, marginBottom: theme.spacing[2] }}>
                  <strong style={{ color: theme.colors.light.text.primary }}>Type:</strong> {selectedWeapon.currentDeployment.clientType}
                </p>
                {selectedWeapon.currentDeployment.deployedDate && (
                  <p style={{ color: theme.colors.light.text.primary }}>
                    <strong style={{ color: theme.colors.light.text.primary }}>Deployed Date:</strong> {
                      (() => {
                        try {
                          let date: Date;
                          const deployedDate = selectedWeapon.currentDeployment.deployedDate;
                          
                          if (deployedDate instanceof Date) {
                            date = deployedDate;
                          } else if (deployedDate && typeof deployedDate === 'object' && typeof deployedDate.toDate === 'function') {
                            date = deployedDate.toDate();
                          } else if (deployedDate && typeof deployedDate === 'object' && (deployedDate.seconds || deployedDate._seconds)) {
                            const seconds = deployedDate.seconds || deployedDate._seconds || 0;
                            const nanoseconds = deployedDate.nanoseconds || deployedDate._nanoseconds || 0;
                            date = new Date(seconds * 1000 + nanoseconds / 1000000);
                          } else {
                            date = new Date(deployedDate);
                          }
                          
                          return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                        } catch {
                          return 'Invalid Date';
                        }
                      })()
                    }
                  </p>
                )}
              </div>
            </div>
          )}

          {selectedWeapon.history && selectedWeapon.history.length > 0 ? (
            <div>
              <h3 style={{ 
                fontSize: theme.typography.fontSize.lg, 
                marginBottom: theme.spacing[2],
                color: theme.colors.light.text.primary,
                fontWeight: theme.typography.fontWeight.bold,
              }}>
                Previous Deployments
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                {selectedWeapon.history.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      padding: theme.spacing[3],
                      background: theme.colors.light.background.secondary,
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.colors.light.border.light}`,
                    }}
                  >
                    <p style={{ color: theme.colors.light.text.primary, marginBottom: theme.spacing[1] }}>
                      <strong style={{ color: theme.colors.light.text.primary }}>Client:</strong> {entry.clientName}
                    </p>
                    <p style={{ color: theme.colors.light.text.primary, marginBottom: theme.spacing[1] }}>
                      <strong style={{ color: theme.colors.light.text.primary }}>Type:</strong> {entry.clientType}
                    </p>
                    <p style={{ color: theme.colors.light.text.primary, marginBottom: theme.spacing[1] }}>
                      <strong style={{ color: theme.colors.light.text.primary }}>Deployed:</strong> {
                        (() => {
                          try {
                            let date: Date;
                            const deployedDate = entry.deployedDate;
                            
                            if (deployedDate instanceof Date) {
                              date = deployedDate;
                            } else if (deployedDate && typeof deployedDate === 'object' && typeof deployedDate.toDate === 'function') {
                              date = deployedDate.toDate();
                            } else if (deployedDate && typeof deployedDate === 'object' && (deployedDate.seconds || deployedDate._seconds)) {
                              const seconds = deployedDate.seconds || deployedDate._seconds || 0;
                              const nanoseconds = deployedDate.nanoseconds || deployedDate._nanoseconds || 0;
                              date = new Date(seconds * 1000 + nanoseconds / 1000000);
                            } else {
                              date = new Date(deployedDate);
                            }
                            
                            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                          } catch {
                            return 'Invalid Date';
                          }
                        })()
                      }
                    </p>
                    <p style={{ color: theme.colors.light.text.primary }}>
                      <strong style={{ color: theme.colors.light.text.primary }}>Returned:</strong> {
                        (() => {
                          try {
                            let date: Date;
                            const returnedDate = entry.returnedDate;
                            
                            if (returnedDate instanceof Date) {
                              date = returnedDate;
                            } else if (returnedDate && typeof returnedDate === 'object' && typeof returnedDate.toDate === 'function') {
                              date = returnedDate.toDate();
                            } else if (returnedDate && typeof returnedDate === 'object' && (returnedDate.seconds || returnedDate._seconds)) {
                              const seconds = returnedDate.seconds || returnedDate._seconds || 0;
                              const nanoseconds = returnedDate.nanoseconds || returnedDate._nanoseconds || 0;
                              date = new Date(seconds * 1000 + nanoseconds / 1000000);
                            } else {
                              date = new Date(returnedDate);
                            }
                            
                            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                          } catch {
                            return 'Invalid Date';
                          }
                        })()
                      }
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState>
              <p style={{ color: theme.colors.light.text.secondary }}>No deployment history available.</p>
            </EmptyState>
          )}
        </Modal>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setWeaponToDelete(null);
        }}
        title="Delete Weapon"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setWeaponToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], marginBottom: theme.spacing[4] }}>
          <FiAlertCircle size={24} style={{ color: theme.colors.accent.warning }} />
          <div>
            <p>Are you sure you want to delete this weapon?</p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              Weapon ID: {weaponToDelete?.weaponId}
            </p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.accent.danger, marginTop: theme.spacing[2] }}>
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default WeaponInventoryPage;

